import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import DepartmentImage from '../../../assets/image/department/portrait-young-woman-using-mobile-phone-while-sitting-cafe_1048944-30787175.jpg';
import DepartmentImage2 from '../../../assets/image/department/focused-african-american-man-with-headset-looking-down_74855-4198.jpg';

function EmploymentWorkforce() {

  const [Faq, setFaq] = useState(0);

  // theme colors from DOM
  const titleRef = useRef(null);
  const bodyRef = useRef(null);
  const [titleColor, setTitleColor] = useState('');
  const [bodyColor, setBodyColor] = useState('');

  useEffect(() => {
    if (titleRef.current) setTitleColor(window.getComputedStyle(titleRef.current).color);
    if (bodyRef.current) setBodyColor(window.getComputedStyle(bodyRef.current).color);
  }, []);

  const overviewEmphasis = { color: titleColor || '#0f172a' };

  // compact stacked subtitle/description layout
  const liRow = { display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '12px' };
  const icon = { marginTop: '4px', flex: '0 0 auto' };
  const kv = { display: 'flex', flexDirection: 'column', gap: '4px', flex: 1, minWidth: 0 };
  const keyText = { fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.35, color: titleColor || '#0f172a' };
  const valText = { fontSize: '0.95rem', lineHeight: 1.6, color: bodyColor || undefined };

  const Drop = [
    {
      Que: "How can I access job training programs?",
      Answer:
        "Use the online portal or visit a local career center. We offer short-term bootcamps, apprenticeships, on-the-job training (OJT), industry certifications, digital skills courses, and incumbent-worker upskilling. Staff help match you to programs that align with your goals and eligibility."
    },
    {
      Que: "What resources are available for career counseling?",
      Answer:
        "Free 1:1 coaching, career assessments, résumé/interview prep, job search strategy, and referrals to training or apprenticeships. Counselors also review labor-market data so you can target high-demand roles with clear wage growth."
    },
    {
      Que: "How does the department assist in workforce development?",
      Answer:
        "We partner with employers, colleges, and training providers to design sector pathways (healthcare, IT, construction, manufacturing, public safety, and more). We coordinate hiring events, rapid-response services for layoffs, and pipelines for youth, veterans, justice-involved, and people with disabilities."
    },
    {
      Que: "Do you help with childcare or transportation while in training?",
      Answer:
        "Yes. Based on eligibility and funding, we can provide stipends or referrals for childcare, transit passes, fuel cards, or other supports that reduce barriers to completion."
    },
    {
      Que: "Are services available in other languages or accessible formats?",
      Answer:
        "Yes. Interpreter services, translated materials, accessible facilities, and reasonable accommodations are available. Let us know your needs when you schedule."
    },
    {
      Que: "Are these services free?",
      Answer:
        "Many services are free. Some trainings have costs covered by grants, employer sponsorships, or scholarships. We’ll check eligibility and funding before you enroll."
    },
    {
      Que: "How do I get started?",
      Answer:
        "Create a profile on the portal, book an intake appointment, bring ID and a recent résumé (if available), and complete a skills and interests assessment. We’ll build a plan with you."
    }
  ];

  return (
    <>
      <section className="department-details-section">
        <div className="container">
          <div className="row">

            {/* Sidebar */}
            <div className="col-12 col-lg-4 col-xl-4">
              <Sidebar />
            </div>

            {/* Main Content */}
            <div className="col-lg-8">
              <div className="department-details-imgbox">
                <img src={DepartmentImage} className="img-fluid" alt="Employment and Workforce Development" />
              </div>

              <div className="department-details-content-box">
                <h4 ref={titleRef} className="department-details-title">Employment and Workforce Development</h4>
                <p style={overviewEmphasis}>
                  We equip residents with skills that lead to good jobs and help employers find qualified talent.
                  Priorities: rapid reemployment, credential attainment, and equitable access to high-demand careers.
                </p>
                <p>
                  Core functions include job training and certification pathways, apprenticeships and OJT, career coaching,
                  labor-market intelligence, employer engagement, youth and veteran programs, and business services for hiring,
                  retention, and upskilling. We align funding with regional demand so training leads to real placements and wage gains.
                </p>
              </div>

              <div className="department-details-box">
                <div className="department-details-policy">
                  <span>
                    Providing comprehensive resources for career growth and economic development.
                  </span>
                </div>
                <p ref={bodyRef}>
                  We bridge job seekers and employers through targeted training, hiring events, and supportive services like
                  childcare and transportation assistance. Our team coordinates with colleges, unions, and industry to shorten
                  time-to-hire, boost retention, and expand opportunities for underrepresented workers.
                </p>
              </div>

              <div className="department-details-benefits-inner-box">
                <div className="row row-gutter-30">
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box-image">
                      <img src={DepartmentImage2} className="img-fluid" alt="Workforce Training" />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="department-details-benefits-box">
                      <h3>Our Benefits</h3>
                      <p>
                        Practical training, faster job matches, and a stronger local talent pipeline.
                      </p>
                      <ul className="list-unstyled list-style" style={{ marginTop: '-15px' }}>
                        <li>
                          <Link to="/department/employment/job-training">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Job Training and Skills Development
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/employment/youth-employment">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Youth and Student Employment Initiatives
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/employment/career-counseling">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Career Counseling and Job Placement Services
                          </Link>
                        </li>
                        <li>
                          <Link to="/department/employment/employer-partnerships">
                            <i className="fa-solid fa-circle-arrow-right" style={{ marginRight: '5px' }} />
                            Employer Partnerships and Workforce Resources
                          </Link>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Outcomes section */}
              <div className="department-details-content-box" style={{ marginTop: '10px' }}>
                <h4
                  className="department-details-title"
                  style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}
                >
                  Outcomes we aim for
                </h4>
                <p style={{ color: bodyColor || undefined, fontSize: '0.95rem' }}>
                  Higher placement rates and median wages, shorter unemployment duration, more industry-recognized credentials
                  and apprenticeship starts, stronger employer satisfaction and retention, reduced hard-to-fill vacancies,
                  and equitable results across demographics and neighborhoods.
                </p>
              </div>

              {/* What We Do — compact stacked layout */}
              <div className="department-details-benefits-box">
                <h3>What We Do</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Career Coaching & Job Match</div>
                      <div style={valText}>
                        Personalized plans, résumé/interview prep, job search strategy, and direct referrals to vetted openings with
                        real-time labor-market insights.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Training, Credentials & Apprenticeships</div>
                      <div style={valText}>
                        Short-term bootcamps, certificate programs, OJT wage subsidies, incumbent-worker upskilling, and registered
                        apprenticeships aligned to high-demand occupations.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Employer Services & Sector Partnerships</div>
                      <div style={valText}>
                        Customized recruitment, hiring events, candidate screening, and co-designed curricula with employers in
                        healthcare, IT, skilled trades, manufacturing, logistics, and public sector.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Rapid Response & Reemployment</div>
                      <div style={valText}>
                        Layoff-aversion support, on-site workshops, UI navigation, re-skilling pathways, and fast-track placement to
                        minimize income disruption.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Inclusive Workforce Supports</div>
                      <div style={valText}>
                        Youth internships, veteran transition services, disability accommodations, second-chance employment, ESL and
                        digital literacy, plus help with childcare and transportation.
                      </div>
                    </div>
                  </li>

                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Labor-Market Intelligence & Career Pathways</div>
                      <div style={valText}>
                        Local wage and vacancy trends, skill requirements, stackable credentials, and advancement ladders so training
                        maps to real jobs and careers.
                      </div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Who We Serve */}
              <div className="department-details-benefits-box">
                <h3>Who We Serve</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Job Seekers & Career Changers</div>
                      <div style={valText}>Recent graduates, mid-career transitions, and workers returning after a break.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Priority Populations</div>
                      <div style={valText}>Youth, veterans, people with disabilities, justice-involved, and dislocated workers.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Employers & Small Businesses</div>
                      <div style={valText}>Hiring, training design, wage subsidies, and retention strategies.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Services for Employers */}
              <div className="department-details-benefits-box">
                <h3>Services for Employers</h3>
                <ul className="list-unstyled list-style">
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Recruitment & Hiring Events</div>
                      <div style={valText}>Targeted outreach, candidate screening, and on-site or virtual job fairs.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Training Incentives</div>
                      <div style={valText}>OJT reimbursements, incumbent-worker training, and apprenticeship sponsorships.</div>
                    </div>
                  </li>
                  <li style={liRow}>
                    <i className="fa-solid fa-circle-arrow-right" style={icon} />
                    <div style={kv}>
                      <div style={keyText}>Retention & Advancement</div>
                      <div style={valText}>Upskilling plans, supervisory training, and career-ladder design to reduce turnover.</div>
                    </div>
                  </li>
                </ul>
              </div>

              {/* How to Get Started */}
              <div className="department-details-content-box">
                <h4 className="department-details-title" style={{ color: titleColor || '#0f172a', marginBottom: '6px' }}>
                  How to Get Started
                </h4>
                <ol style={{ paddingLeft: '18px', color: bodyColor || undefined, fontSize: '0.95rem', lineHeight: 1.6 }}>
                  <li>Create a profile on the workforce portal and select your interests.</li>
                  <li>Schedule a virtual or in-person intake with a career coach.</li>
                  <li>Bring ID and any résumés/certificates; complete skills and interest assessments.</li>
                  <li>Enroll in training or get matched to openings; line up supports (childcare/transport).</li>
                  <li>Interview, start work, and continue upskilling with stackable credentials.</li>
                </ol>
              </div>

              <div className="accordian-box">
                {Drop.map((items, index) => (
                  <div className={`accordian-box-item ${Faq !== index && "active"}`} key={index}>
                    <div className="accordian-title">
                      <h5>{items.Que}</h5>
                      <i className="fa-solid fa-angle-right" onClick={() => setFaq(index)} />
                    </div>

                    {Faq === index && (
                      <div className="accordian-content" style={{ display: "block" }}>
                        <p>{items.Answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default EmploymentWorkforce;
